/**
 * The measuring side of the annotation sync: what each leg of a propagation
 * costs, in one place, so a latency complaint can name a leg.
 *
 * "Sync takes quite a while" was as much as the session that opened LWR-26720
 * could say. A change made here travels through a debounce, a serial write
 * queue, an XFDF export, a POST, the API's own work, a queue worker, Pusher,
 * and an import on every other participant's viewer — and for a signature,
 * whose image never fits the broadcast, a second round trip on top. Any one of
 * those could account for seconds, and none of them was timed.
 *
 * Nothing here talks to Sentry. The implementation that does lives in
 * plugins/09-sync-telemetry.client.ts and is injected, which keeps the browser
 * SDK out of the unit tests of the composable this measures — they run with the
 * no-op below and behave exactly as they did before.
 */

export type SpanAttributes = Record<string, string | number | boolean | null | undefined>;

/** The trace context a broadcast carries, so the receiver can continue it. */
export interface BroadcastTraceContext {
  sentryTrace: string;
  baggage: string;
}

export interface SyncSpanOptions {
  /** What this span measures, e.g. `annotation.sync.post`. */
  op: string;
  /** Human-readable name, defaulting to the op. */
  name?: string;
  /**
   * When the work being measured actually began, as an epoch millisecond. Lets
   * a wait be recorded after the fact: the queue already knows when a change
   * was made, so the debounce in front of it needs no bookkeeping of its own.
   */
  startTime?: number;
  attributes?: SpanAttributes;
  /**
   * Start a trace of this span's own rather than joining whatever the page was
   * already doing. A flush belongs to the change that caused it, not to the
   * navigation that happened to be the last thing traced.
   */
  newTrace?: boolean;
  /** The sender's trace, to continue on this side of a broadcast. */
  trace?: BroadcastTraceContext | null;
}

export interface SyncTelemetry {
  /**
   * Runs the work as a span. Returns exactly what the work returns, sync or
   * async alike: an await introduced here would reorder the composable this
   * measures, which is not something a measurement is allowed to do.
   */
  span<T>(options: SyncSpanOptions, work: () => T): T;
  /** Records a leg that has already happened, given when it started. */
  leg(op: string, startedAtMs: number, attributes?: SpanAttributes): void;
  /**
   * Records a leg that began at a moment given on the server's clock, which is
   * the only way to time the trip from another participant's viewer to this
   * one: the two machines' own clocks can be seconds apart.
   */
  legFromServer(op: string, serverStartedAtMs: number, attributes?: SpanAttributes): void;
  /**
   * Reports an answer that carried the server's clock, so the offset between
   * this machine and the one every participant shares can be estimated.
   */
  observeServerClock(requestStartedAtMs: number, serverTimeMs: number): void;
  /** The clock every measurement here is taken from. */
  now(): number;
  /**
   * This machine's clock read in server time, or its raw clock while no answer
   * has reported the server's yet.
   *
   * This is the clock competing edits are ordered by, not just measured with:
   * participants' wall clocks can be minutes apart, and an edit stamped with a
   * raw clock that runs behind another participant's loses to it forever.
   */
  serverNow(): number;
}

/** What the composable gets when nothing is measuring. */
export const noopTelemetry: SyncTelemetry = {
  span: (_options, work) => work(),
  leg: () => {},
  legFromServer: () => {},
  observeServerClock: () => {},
  now: () => Date.now(),
  serverNow: () => Date.now(),
};

/**
 * A duration that ran longer than this is not a leg, it is a clock that moved:
 * an NTP correction or a laptop coming out of sleep mid-session. Recording it
 * would put a number in the p95 that nothing on the network ever took.
 */
const MAX_PLAUSIBLE_LEG_MS = 60_000;

/** The duration to record for a leg, or null when the clock cannot be trusted. */
export const legDuration = (startedAtMs: number, endedAtMs: number): number | null => {
  const elapsed = endedAtMs - startedAtMs;

  if (elapsed > MAX_PLAUSIBLE_LEG_MS) return null;

  // A clock that stepped backwards mid-leg reports the work as instant rather
  // than as negative, which is the honest reading of "no time we can measure".
  return Math.max(0, elapsed);
};

/**
 * The value at the given percentile, by nearest rank.
 *
 * Exported, and the only place a percentile is computed, so the before and the
 * after of this ticket are compared with the same arithmetic. Nearest rank and
 * not an interpolation: it always returns a measurement that was actually
 * taken, which is what a threshold is argued about.
 */
export const percentile = (values: number[], p: number): number | null => {
  if (!values.length) return null;

  const sorted = [...values].sort((a, b) => a - b);
  const rank = Math.max(0, Math.ceil(p * sorted.length) - 1);

  return sorted[Math.min(rank, sorted.length - 1)];
};

/**
 * How far this machine's clock is from the API's.
 *
 * A propagation is timed across three machines, and two participants' clocks
 * can be seconds apart — more than the threshold this is measured against. Each
 * client estimates its own offset to the one clock they all talk to, so their
 * numbers can be subtracted from one another.
 *
 * The estimate comes from the round trip of an answer that reported the server
 * clock: the server's stamp was taken somewhere inside it, and the midpoint of
 * the request is the best guess available. The sample kept is the one with the
 * shortest round trip, since that is the one whose midpoint is least wrong —
 * the same reason NTP keeps it.
 */
export const createClockOffset = () => {
  let offsetMs = 0;
  let bestRoundTripMs = Number.POSITIVE_INFINITY;

  const observe = (requestStartedAtMs: number, serverTimeMs: number, now = Date.now()) => {
    const roundTripMs = now - requestStartedAtMs;
    if (roundTripMs < 0 || roundTripMs >= bestRoundTripMs) return;

    bestRoundTripMs = roundTripMs;
    offsetMs = Math.round(serverTimeMs - (requestStartedAtMs + now) / 2);
  };

  /** The local clock read in server time, or null while nothing is known. */
  const toServer = (localMs: number): number | null =>
    Number.isFinite(bestRoundTripMs) ? localMs + offsetMs : null;

  /** A server-clock moment read on this machine's clock. */
  const toLocal = (serverMs: number): number | null =>
    Number.isFinite(bestRoundTripMs) ? serverMs - offsetMs : null;

  /**
   * How wrong the offset may be: the server's stamp sits somewhere inside the
   * round trip, so half of it is the worst the midpoint can be off by. Reported
   * next to every measurement rather than hidden, because a p95 quoted against
   * a two second threshold is only as good as the clocks behind it.
   */
  const uncertaintyMs = (): number | null =>
    Number.isFinite(bestRoundTripMs) ? Math.round(bestRoundTripMs / 2) : null;

  return {
    observe,
    toServer,
    toLocal,
    uncertaintyMs,
    offsetMs: () => (Number.isFinite(bestRoundTripMs) ? offsetMs : null),
  };
};

export type ClockOffset = ReturnType<typeof createClockOffset>;

/** One measured leg, as a row of the export. */
export interface LatencyRow {
  role: string;
  leg: string;
  durationMs: number;
  localMs: number;
  serverMs: number | null;
  clockOffsetMs: number | null;
  clockUncertaintyMs: number | null;
  attributes: SpanAttributes;
}

const CSV_COLUMNS = [
  "role",
  "leg",
  "duration_ms",
  "local_ms",
  "server_ms",
  "clock_offset_ms",
  "clock_uncertainty_ms",
  "change_key",
  "annotation_type",
  "reconcile_reason",
  "revision",
  "bytes",
  "count",
  "client_id",
] as const;

const csvCell = (value: unknown): string => {
  if (value === null || value === undefined) return "";

  const text = String(value);

  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * The last few hundred legs, kept in the page.
 *
 * Sentry is where these numbers live day to day, but the measurement this
 * ticket has to produce is a p95 over a scripted run of twenty actions, read
 * off three browsers minutes later. Waiting on sampling and indexing for that
 * is how a run gets thrown away, so the run is also recorded here and exported
 * as a CSV from the console. Nothing of the annotations themselves is kept:
 * ids, sizes and counts only.
 */
export const createLatencyLog = (clock: ClockOffset, capacity = 500) => {
  const rows: LatencyRow[] = [];

  const push = (row: Omit<LatencyRow, "serverMs" | "clockOffsetMs" | "clockUncertaintyMs">) => {
    rows.push({
      ...row,
      serverMs: clock.toServer(row.localMs),
      clockOffsetMs: clock.offsetMs(),
      clockUncertaintyMs: clock.uncertaintyMs(),
    });

    if (rows.length > capacity) rows.splice(0, rows.length - capacity);
  };

  const cellOf = (row: LatencyRow, column: (typeof CSV_COLUMNS)[number]) => {
    switch (column) {
      case "role":
        return row.role;
      case "leg":
        return row.leg;
      case "duration_ms":
        return row.durationMs;
      case "local_ms":
        return row.localMs;
      case "server_ms":
        return row.serverMs;
      case "clock_offset_ms":
        return row.clockOffsetMs;
      case "clock_uncertainty_ms":
        return row.clockUncertaintyMs;
      default:
        // The rest are whatever the leg had to say about itself, under the
        // snake_case name the reduction script reads.
        return row.attributes[column];
    }
  };

  const csv = (): string =>
    [
      CSV_COLUMNS.join(","),
      ...rows.map((row) => CSV_COLUMNS.map((column) => csvCell(cellOf(row, column))).join(",")),
    ].join("\n");

  /** p50, p95, max and n per leg: the shape the ticket's table is filled from. */
  const summary = () => {
    const byLeg = new Map<string, number[]>();
    for (const row of rows) {
      byLeg.set(row.leg, [...(byLeg.get(row.leg) ?? []), row.durationMs]);
    }

    return [...byLeg.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([leg, durations]) => ({
        leg,
        n: durations.length,
        p50: percentile(durations, 0.5),
        p95: percentile(durations, 0.95),
        max: Math.max(...durations),
      }));
  };

  return {
    push,
    rows: () => [...rows],
    json: () => JSON.stringify(rows),
    csv,
    summary,
    clear: () => rows.splice(0, rows.length),
  };
};

export type LatencyLog = ReturnType<typeof createLatencyLog>;
